import React, { useEffect, useRef } from "react";
import { ChevronUp20Regular, ChevronDown20Regular, Dismiss20Regular } from "@fluentui/react-icons";
import styles from "./ChatFindBar.module.css";

interface Props {
  query: string;
  onQueryChange: (q: string) => void;
  /** 0-based index of the active match */
  currentIndex: number;
  total: number;
  onNext: () => void;
  onPrev: () => void;
  onClose: () => void;
}

export const ChatFindBar: React.FC<Props> = ({
  query,
  onQueryChange,
  currentIndex,
  total,
  onNext,
  onPrev,
  onClose,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus the input as soon as the bar opens
  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.shiftKey ? onPrev() : onNext();
    } else if (e.key === "Escape") {
      onClose();
    }
  };

  const matchLabel =
    total === 0
      ? query.trim()
        ? "No results"
        : ""
      : `${currentIndex + 1} of ${total}`;

  const noResults = query.trim() !== "" && total === 0;

  return (
    <div className={styles.findBar} role="search" aria-label="Find in chat">
      <input
        ref={inputRef}
        type="text"
        className={`${styles.findInput} ${noResults ? styles.findInputNoResults : ""}`}
        placeholder="Find in chat…"
        value={query}
        onChange={(e) => onQueryChange(e.target.value)}
        onKeyDown={handleKeyDown}
        aria-label="Search text"
        aria-live="polite"
        autoComplete="off"
        spellCheck={false}
      />
      <span className={`${styles.matchCount} ${noResults ? styles.matchCountNoResults : ""}`}>
        {matchLabel}
      </span>
      <div className={styles.navGroup}>
        <button
          className={styles.navBtn}
          onClick={onPrev}
          title="Previous match (Shift+Enter)"
          disabled={total === 0}
          aria-label="Previous match"
        >
          <ChevronUp20Regular />
        </button>
        <button
          className={styles.navBtn}
          onClick={onNext}
          title="Next match (Enter)"
          disabled={total === 0}
          aria-label="Next match"
        >
          <ChevronDown20Regular />
        </button>
      </div>
      <button
        className={styles.closeBtn}
        onClick={onClose}
        title="Close (Esc)"
        aria-label="Close find bar"
      >
        <Dismiss20Regular />
      </button>
    </div>
  );
};
